// src/SallerAdmin/pages/Profile/UserProfilePage.tsx
import React, { useState, useEffect } from 'react';
import { useUser } from '../../../contexts/UserContext'; 
import { Link } from 'react-router-dom';
import { 
    Mail, Phone, MapPin, User as UserIcon, Lock, Edit, 
    Trash, Plus, X, CheckCircle, AlertTriangle, AlertCircle 
} from 'lucide-react'; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'; 
import { faCog } from '@fortawesome/free-solid-svg-icons'; 

// --- Helper Components ---
const DetailCard = ({ title, children, action }: any) => (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm h-full flex flex-col transition-shadow hover:shadow-md">
        <div className="p-4 md:p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 rounded-t-xl">
            <h3 className="text-base md:text-lg font-bold text-gray-800">{title}</h3>
            {action}
        </div>
        <div className="p-4 md:p-5 space-y-4 flex-grow relative">
            {children}
        </div>
    </div>
);

const DetailRow = ({ icon, label, value, isPrimary=false }: any) => (
    <div className={`flex items-start space-x-3 p-2.5 rounded-lg transition-colors ${isPrimary ? 'bg-blue-50/60 border border-blue-100' : 'hover:bg-gray-50'}`}>
        <div className="text-[#00635e] flex-shrink-0 mt-0.5">{icon}</div> 
        <div className="flex-1 min-w-0">
            <p className="text-[10px] md:text-xs font-bold text-gray-500 uppercase flex items-center mb-0.5">
                {label} {isPrimary && <span className="ml-2 text-[9px] bg-blue-600 text-white px-1.5 py-0.5 rounded-full tracking-wider">PRIMARY</span>}
            </p>
            <p className="text-sm font-semibold text-gray-900 break-words">{value || <span className="text-gray-400 italic">Not Added</span>}</p>
        </div>
    </div>
);

// --- TOAST NOTIFICATION COMPONENT ---
const Toast = ({ show, message, type, onClose }: any) => {
    if (!show) return null;
    const isSuccess = type === 'success';
    return (
        <div className={`fixed top-6 left-1/2 transform -translate-x-1/2 z-[100] flex items-center gap-3 px-6 py-4 rounded-xl shadow-2xl border animate-in slide-in-from-top-5 fade-in duration-300 ${isSuccess ? 'bg-white border-green-200 text-green-800' : 'bg-white border-red-200 text-red-800'}`}>
            <div className={`p-2 rounded-full ${isSuccess ? 'bg-green-100' : 'bg-red-100'}`}>
                {isSuccess ? <CheckCircle size={20} className="text-green-600"/> : <AlertCircle size={20} className="text-red-600"/>}
            </div>
            <div>
                <h4 className="text-sm font-bold">{isSuccess ? 'Success' : 'Error'}</h4>
                <p className="text-xs font-medium opacity-90">{message}</p>
            </div>
            <button onClick={onClose} className="ml-4 p-1 hover:bg-black/5 rounded-full"><X size={14}/></button>
        </div>
    );
};

const UserProfilePage: React.FC = () => {
    const { user, updateUser } = useUser(); 
    
    // States
    const [loading, setLoading] = useState(false);
    const [addresses, setAddresses] = useState<any[]>([]);
    
    // Modals & Notifications
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPassModal, setShowPassModal] = useState(false);
    const [showAddrModal, setShowAddrModal] = useState(false);
    
    // Toast State
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    
    // Confirmation Modal State (For Deleting Address)
    const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

    // Form Data
    const [editData, setEditData] = useState<any>({});
    const [passData, setPassData] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
    const [newAddress, setNewAddress] = useState({ addressLine1: '', city: '', state: '', postalCode: '' });
    const [locLoading, setLocLoading] = useState(false);

    // --- INIT ---
    useEffect(() => {
        if(user) {
            setEditData({
                username: user.username,
                mobile: user.mobile,
                alternateMobile: user.alternateMobile || '',
                email: user.email,
                alternateEmail: user.alternateEmail || '',
                companyName: user.companyName
            });
            fetchAddresses();
        }
    }, [user]);

    const getToken = () => localStorage.getItem("token");

    // Helper to show toast
    const showToast = (message: string, type: 'success' | 'error' = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000); // Auto hide after 3s
    };

    // --- API CALLS ---
    const fetchAddresses = async () => {
        try {
            const res = await fetch(`/springboot/api/seller/profile/${user?.id}/addresses`, {
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if(res.ok) setAddresses(await res.json());
        } catch(e) { console.error(e); }
    };

    // --- HANDLERS: PROFILE EDIT ---
    const handleEditChange = (e: any) => {
        setEditData({...editData, [e.target.name]: e.target.value});
    };

    const handleSwapMobile = () => {
        setEditData({ ...editData, mobile: editData.alternateMobile, alternateMobile: editData.mobile });
    };

    const handleSwapEmail = () => {
        setEditData({ ...editData, email: editData.alternateEmail, alternateEmail: editData.email });
    };

    const saveProfile = async () => {
        setLoading(true);
        try {
            const res = await fetch(`/springboot/api/seller/profile/${user?.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
                body: JSON.stringify(editData)
            });
            if(res.ok) {
                const updated = await res.json();
                updateUser(updated); 
                setShowEditModal(false);
                showToast("Profile updated successfully!");
            } else {
                showToast("Failed to update profile.", "error");
            }
        } catch(e) { showToast("Something went wrong.", "error"); }
        finally { setLoading(false); }
    };

    // --- HANDLERS: PASSWORD ---
    const changePassword = async () => {
        if(passData.newPassword !== passData.confirmPassword) {
            return showToast("New passwords do not match!", "error");
        }
        setLoading(true);
        try {
            const res = await fetch(`/springboot/api/seller/profile/${user?.id}/change-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
                body: JSON.stringify(passData)
            });
            if(res.ok) {
                showToast("Password changed successfully!");
                setShowPassModal(false);
                setPassData({ oldPassword: '', newPassword: '', confirmPassword: '' });
            } else {
                showToast(await res.text(), "error");
            }
        } catch(e) { showToast("Error changing password.", "error"); }
        finally { setLoading(false); }
    };

    // --- HANDLERS: ADDRESS ---
    const handlePincodeChange = async (e: any) => {
        const val = e.target.value.replace(/\D/g, '');
        setNewAddress({...newAddress, postalCode: val});
        if(val.length === 6) {
            setLocLoading(true);
            try {
                const res = await fetch(`https://api.postalpincode.in/pincode/${val}`);
                const data = await res.json();
                if(data[0].Status === "Success") {
                    setNewAddress(prev => ({...prev, postalCode: val, city: data[0].PostOffice[0].District, state: data[0].PostOffice[0].State}));
                }
            } finally { setLocLoading(false); }
        }
    };

    const saveAddress = async () => {
        if(!newAddress.postalCode || !newAddress.addressLine1) return showToast("Please fill all details", "error");
        try {
            const res = await fetch(`/springboot/api/seller/profile/${user?.id}/addresses`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
                body: JSON.stringify(newAddress)
            });
            if(res.ok) {
                fetchAddresses();
                setShowAddrModal(false);
                setNewAddress({ addressLine1: '', city: '', state: '', postalCode: '' });
                showToast("New address added!");
            }
        } catch(e) { showToast("Failed to save address", "error"); }
    };

    // Trigger confirmation modal instead of window.confirm
    const confirmDeleteAddress = (id: number) => {
        setConfirmDeleteId(id);
    };

    // Actual Delete Logic
    const executeDeleteAddress = async () => {
        if (confirmDeleteId === null) return;
        try {
            await fetch(`/springboot/api/seller/profile/addresses/${confirmDeleteId}`, { method: 'DELETE', headers: {'Authorization': `Bearer ${getToken()}`} });
            fetchAddresses();
            showToast("Address deleted successfully.");
        } catch(e) {
            showToast("Failed to delete address.", "error");
        } finally {
            setConfirmDeleteId(null);
        }
    };

    if(!user) return <div className="p-8 text-center text-gray-500">Loading Profile...</div>;

    const userInitial = user.username ? user.username.charAt(0).toUpperCase() : 'S';

    return (
        <div className=" bg-gray-50/50 min-h-screen font-sans relative">
            
            {/* --- TOAST COMPONENT --- */}
            <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />

            <h1 className="text-xl md:text-2xl font-black text-[#00635e] mb-6 border-b border-gray-200 pb-4 flex items-center">
                <UserIcon className="mr-3 text-red-600" size={28} />
                User Profile <span className="ml-2 text-sm md:text-base font-medium text-gray-400 font-sans hidden sm:inline-block">(Personal Details)</span>
            </h1>

            {/* --- Main Responsive Grid --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* 1. Account Holder */}
                <div className="md:col-span-2 lg:col-span-1">
                    <DetailCard 
                        title="Account Holder Details"
                        action={
                            <button onClick={()=>setShowEditModal(true)} className="text-[#00635e] text-xs md:text-sm font-bold hover:text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-all flex items-center">
                                <Edit size={14} className="mr-1.5"/> Edit
                            </button>
                        }
                    >
                        <div className="flex items-center space-x-4 pb-4 border-b border-gray-100 mb-2">
                            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#00635e] to-teal-400 flex items-center justify-center text-white font-bold text-2xl shadow-lg border-2 border-white ring-2 ring-teal-50">
                                {userInitial}
                            </div>
                            <div className="min-w-0">
                                <p className="text-lg md:text-xl font-bold text-gray-900 truncate">{user.username}</p>
                                <p className="text-xs md:text-sm text-gray-500 font-medium truncate">{user.companyName || 'Seller Account'}</p>
                            </div>
                        </div>
                        <div className="space-y-1">
                            <DetailRow icon={<Mail size={16} />} label="Primary Email" value={user.email} isPrimary />
                            <DetailRow icon={<Mail size={16} />} label="Alternate Email" value={user.alternateEmail} />
                            <DetailRow icon={<Phone size={16} />} label="Contact Number" value={user.mobile} isPrimary />
                            <DetailRow icon={<Phone size={16} />} label="Alternate Contact" value={user.alternateMobile} />
                        </div>
                    </DetailCard>
                </div>

                {/* 2. Security */}
                <div className="md:col-span-1">
                    <DetailCard title="Account Security">
                        <div className="p-5 bg-orange-50 rounded-xl border border-orange-100 h-full flex flex-col justify-between">
                            <div>
                                <div className="flex items-center mb-3 text-orange-800 font-bold">
                                    <Lock size={18} className="mr-2"/> Password Status
                                </div>
                                <p className="text-xs md:text-sm text-orange-700/80 mb-4 leading-relaxed">
                                    Regular password updates protect your business account from unauthorized access. We recommend changing it every 90 days.
                                </p>
                            </div>
                            <button onClick={()=>setShowPassModal(true)} className="w-full bg-white border border-orange-300 text-orange-700 py-2.5 rounded-lg text-sm font-bold hover:bg-orange-600 hover:text-white transition-all shadow-sm">
                                Change Password
                            </button>
                        </div>
                    </DetailCard>
                </div>

                {/* 3. Addresses */}
                <div className="md:col-span-1">
                    <DetailCard 
                        title="Saved Addresses"
                        action={
                            <button onClick={()=>setShowAddrModal(true)} className="text-green-600 text-xs md:text-sm font-bold hover:text-green-700 hover:bg-green-50 px-3 py-1.5 rounded-lg transition-all flex items-center">
                                <Plus size={14} className="mr-1.5"/> Add New
                            </button>
                        }
                    >
                        {addresses.length === 0 ? (
                            <div className="text-center text-gray-400 py-8 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                                <MapPin className="mx-auto mb-2 opacity-30" size={32} />
                                <span className="text-sm font-medium">No addresses saved yet.</span>
                            </div>
                        ) : (
                            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1 custom-scrollbar">
                                {addresses.map((addr, idx) => (
                                    <div key={idx} className="p-3.5 border border-gray-200 rounded-xl bg-white relative group hover:border-[#00635e]/30 transition-all shadow-sm">
                                        <div className="pr-6">
                                            <p className="font-bold text-sm text-gray-800 flex items-center">
                                                <MapPin size={12} className="mr-1.5 text-red-500" />
                                                {addr.city} <span className="text-gray-400 mx-1">•</span> {addr.postalCode}
                                            </p>
                                            <p className="text-xs text-gray-600 mt-1 leading-relaxed">{addr.addressLine1}</p>
                                        </div>
                                        {/* Button triggers modal instead of window.confirm */}
                                        <button 
                                            onClick={()=>confirmDeleteAddress(addr.id)} 
                                            className="absolute top-3 right-3 text-gray-300 hover:text-red-500 md:opacity-0 md:group-hover:opacity-100 transition-all p-1"
                                        >
                                            <Trash size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </DetailCard>
                </div>
            </div>

            {/* --- MODAL: EDIT PROFILE --- */}
            {showEditModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto transform transition-all scale-100">
                        <div className="flex justify-between items-center p-5 border-b sticky top-0 bg-white z-10">
                            <h2 className="text-lg md:text-xl font-bold text-gray-800">Edit Personal Details</h2>
                            <button onClick={()=>setShowEditModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition"><X size={20} className="text-gray-500" /></button>
                        </div>
                        
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                                <label className="block text-[10px] font-black text-blue-800 mb-1.5 tracking-wider">PRIMARY MOBILE</label>
                                <input disabled value={editData.mobile} className="w-full bg-white border border-gray-300 rounded-lg p-2.5 text-gray-500 text-sm mb-4 cursor-not-allowed" />
                                <label className="block text-[10px] font-black text-gray-500 mb-1.5 tracking-wider">ALTERNATE MOBILE</label>
                                <input name="alternateMobile" value={editData.alternateMobile} onChange={handleEditChange} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition" />
                                {editData.alternateMobile && (
                                    <button onClick={handleSwapMobile} className="mt-3 text-xs bg-white border border-blue-200 text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-600 hover:text-white w-full transition font-bold shadow-sm">
                                        Make Alternate Primary
                                    </button>
                                )}
                            </div>
                            <div className="bg-purple-50/50 p-4 rounded-xl border border-purple-100">
                                <label className="block text-[10px] font-black text-purple-800 mb-1.5 tracking-wider">PRIMARY EMAIL</label>
                                <input disabled value={editData.email} className="w-full bg-white border border-gray-300 rounded-lg p-2.5 text-gray-500 text-sm mb-4 cursor-not-allowed" />
                                <label className="block text-[10px] font-black text-gray-500 mb-1.5 tracking-wider">ALTERNATE EMAIL</label>
                                <input name="alternateEmail" value={editData.alternateEmail} onChange={handleEditChange} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-purple-500 outline-none transition" />
                                {editData.alternateEmail && (
                                    <button onClick={handleSwapEmail} className="mt-3 text-xs bg-white border border-purple-200 text-purple-700 px-3 py-2 rounded-lg hover:bg-purple-600 hover:text-white w-full transition font-bold shadow-sm">
                                        Make Alternate Primary
                                    </button>
                                )}
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase">Full Name / Username</label>
                                <input name="username" value={editData.username} onChange={handleEditChange} className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-[#00635e] outline-none transition font-medium" />
                            </div>
                        </div>

                        <div className="p-5 border-t bg-gray-50 flex justify-end gap-3 rounded-b-2xl">
                            <button onClick={()=>setShowEditModal(false)} className="px-5 py-2.5 text-sm font-bold text-gray-600 hover:bg-gray-200 rounded-lg transition">Cancel</button>
                            <button onClick={saveProfile} disabled={loading} className="bg-red-600 text-white px-6 py-2.5 rounded-lg text-sm font-bold hover:bg-red-700 shadow-md active:scale-95 transition-all">
                                {loading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- MODAL: CHANGE PASSWORD --- */}
            {showPassModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                        <div className="p-6">
                            <h2 className="text-xl font-bold text-gray-800 mb-1">Change Password</h2>
                            <p className="text-sm text-gray-500 mb-6">Create a strong password to keep your account safe.</p>
                            <div className="space-y-4">
                                <input type="password" placeholder="Current Password" value={passData.oldPassword} onChange={(e)=>setPassData({...passData, oldPassword: e.target.value})} className="w-full border border-gray-300 p-2.5 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none" />
                                <input type="password" placeholder="New Password" value={passData.newPassword} onChange={(e)=>setPassData({...passData, newPassword: e.target.value})} className="w-full border border-gray-300 p-2.5 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none" />
                                <input type="password" placeholder="Confirm Password" value={passData.confirmPassword} onChange={(e)=>setPassData({...passData, confirmPassword: e.target.value})} className="w-full border border-gray-300 p-2.5 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none" />
                            </div>
                        </div>
                        <div className="p-5 border-t bg-gray-50 flex justify-end space-x-3">
                            <button onClick={()=>setShowPassModal(false)} className="px-4 py-2 text-sm font-bold text-gray-600 hover:bg-gray-200 rounded-lg">Cancel</button>
                            <button onClick={changePassword} disabled={loading} className="bg-orange-500 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-orange-600 shadow-sm active:scale-95 transition">Update</button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- MODAL: ADD ADDRESS --- */}
            {showAddrModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
                         <div className="flex justify-between items-center p-5 border-b">
                            <h2 className="text-xl font-bold text-gray-800">Add New Address</h2>
                            <button onClick={()=>setShowAddrModal(false)} className="hover:bg-gray-100 p-1 rounded-full"><X size={20}/></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="relative">
                                <label className="block text-xs font-bold text-gray-600 mb-1">PINCODE</label>
                                <input placeholder="6 Digits" value={newAddress.postalCode} onChange={handlePincodeChange} maxLength={6} className="w-full border border-gray-300 p-2.5 rounded-lg pr-8 text-sm focus:ring-2 focus:ring-green-500 outline-none" />
                                {locLoading && <FontAwesomeIcon icon={faCog} spin className="absolute right-3 top-9 text-gray-400" />}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <input placeholder="City" value={newAddress.city} readOnly className="w-full border border-gray-200 p-2.5 rounded-lg bg-gray-50 text-gray-500 text-sm" />
                                <input placeholder="State" value={newAddress.state} readOnly className="w-full border border-gray-200 p-2.5 rounded-lg bg-gray-50 text-gray-500 text-sm" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-600 mb-1">FULL ADDRESS</label>
                                <textarea placeholder="House No, Street, Building, Area" value={newAddress.addressLine1} onChange={(e)=>setNewAddress({...newAddress, addressLine1: e.target.value})} className="w-full border border-gray-300 p-2.5 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none resize-none h-24" />
                            </div>
                        </div>
                        <button onClick={saveAddress} className="w-full bg-[#00635e] text-white py-3.5 font-bold hover:bg-teal-800 transition active:bg-teal-900">Save Address</button>
                    </div>
                </div>
            )}

            {/* --- CONFIRM DELETE MODAL --- */}
            {confirmDeleteId !== null && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[150] p-4 animate-in fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden text-center p-6">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AlertTriangle size={32} className="text-red-600" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 mb-2">Delete Address?</h2>
                        <p className="text-sm text-gray-500 mb-6">Are you sure you want to remove this address? This action cannot be undone.</p>
                        
                        <div className="flex gap-3">
                            <button 
                                onClick={() => setConfirmDeleteId(null)} 
                                className="flex-1 py-2.5 border border-gray-300 rounded-xl font-bold text-gray-700 hover:bg-gray-50 transition"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={executeDeleteAddress} 
                                className="flex-1 py-2.5 bg-red-600 rounded-xl font-bold text-white hover:bg-red-700 shadow-lg shadow-red-200 transition"
                            >
                                Yes, Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Footer Navigation */}
            <div className="mt-8 pt-6 border-t border-gray-200 flex justify-start">
                 <Link to="/seller/profile/business" className="inline-flex items-center text-sm font-bold text-[#00635e] hover:text-red-600 bg-white px-4 py-2 rounded-lg border border-gray-200 hover:border-red-200 transition-all shadow-sm group">
                    Go to Business Profile &rarr;
                 </Link>
            </div>
        </div>
    );
};

export default UserProfilePage;